// Shell presentacional de primeiro acesso / ativação de conta (F3-C).
//
// Extraído da FirstAccessPage do web-comite e levado ao admin/recurso. É PURO de
// infra: sem crypto, sem fetch, sem i18n, SEM a lib `qrcode`. Recebe labels (i18n
// local), o otpAuthUri e um gerador de QR injetado (makeQrSvg) — a lib `qrcode`
// fica no wrapper de cada app. A derivação Argon2id + POST /first-access vivem no
// auth-client de cada app, injetados via onSubmit.
//
// Montado com primitivos (Card/Field/Input/Button/Alert) → tematizado e dark-safe
// via tokens reais, sem os aliases --color-* (armadilha de runtime da F3-B, que a
// FirstAccessPage antiga ainda carregava).

import { useEffect, useState, type FormEvent } from 'react';
import { Card } from './Card';
import { Field, Input } from './Input';
import { Button } from './Button';
import { Alert } from './Alert';

/** Mínimo de caracteres da senha (fonte única — apps interpolam labels com isto). */
export const FIRST_ACCESS_MIN_LENGTH = 12;

export interface FirstAccessLabels {
  title: string;
  intro: string;
  emailPrefix: string;
  qrUnavailable: string;
  qrNotProvided: string;
  otpCopyLabel: string;
  passwordLabel: string;
  confirmLabel: string;
  totpLabel: string;
  submit: string;
  submitting: string;
  errTooShort: string;
  errMismatch: string;
  errInvalidTotp: string;
  errTotpIncorrect: string;
  errAlreadyActivated: string;
  errNetwork: string;
  errGeneric: string;
}

/** Resultado da ativação, normalizado pelo app a partir do auth-client. */
export type FirstAccessSubmitResult =
  | { ok: true }
  | { ok: false; error: 'invalid_totp' | 'already_activated' | 'network' | 'other' };

export interface FirstAccessFormProps {
  email: string;
  /** otpauth://totp/... entregue no link. undefined = membro recebe o QR fora-de-banda. */
  otpAuthUri?: string | undefined;
  /** Gerador de QR injetado (lib `qrcode` mora no wrapper). uri → SVG string. */
  makeQrSvg?: ((uri: string) => Promise<string>) | undefined;
  labels: FirstAccessLabels;
  minLength?: number | undefined;
  /** DEV-ONLY — valor inicial do campo TOTP. O app passa '000000' em `vite dev`
   *  (o backend dispensa o código com TOTP_DEV_BYPASS); ausente = campo vazio. */
  devDefaultTotp?: string | undefined;
  /** Deriva o authHash e POSTa /first-access. O shell cuida de estado/validação. */
  onSubmit: (password: string, totpCode: string) => Promise<FirstAccessSubmitResult>;
  /** Conta ativada — navegação a cargo do app (tipicamente volta ao login). */
  onSuccess: () => void;
}

export function FirstAccessForm({
  email,
  otpAuthUri,
  makeQrSvg,
  labels,
  minLength = FIRST_ACCESS_MIN_LENGTH,
  devDefaultTotp,
  onSubmit,
  onSuccess,
}: FirstAccessFormProps): JSX.Element {
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [totpCode, setTotpCode] = useState(devDefaultTotp ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!otpAuthUri || !makeQrSvg) {
      setQrSvg(null);
      return;
    }
    let alive = true;
    void makeQrSvg(otpAuthUri)
      .then((svg) => {
        if (alive) setQrSvg(svg);
      })
      .catch(() => {
        if (alive) setQrSvg(null);
      });
    return () => {
      alive = false;
    };
  }, [otpAuthUri, makeQrSvg]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (password.length < minLength) {
      setError(labels.errTooShort);
      return;
    }
    if (password !== confirm) {
      setError(labels.errMismatch);
      return;
    }
    if (!/^\d{6}$/.test(totpCode)) {
      setError(labels.errInvalidTotp);
      return;
    }
    setSubmitting(true);
    const res = await onSubmit(password, totpCode);
    setSubmitting(false);
    if (!res.ok) {
      if (res.error === 'invalid_totp') setError(labels.errTotpIncorrect);
      else if (res.error === 'already_activated') setError(labels.errAlreadyActivated);
      else if (res.error === 'network') setError(labels.errNetwork);
      else setError(labels.errGeneric);
      return;
    }
    onSuccess();
  };

  return (
    <Card padding="lg" data-testid="first-access">
      <h2 className="cf-card__title" style={{ marginBottom: 'var(--space-2, 8px)' }}>
        {labels.title}
      </h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 'var(--space-4, 16px)' }}>
        {labels.intro}
      </p>

      <Alert variant="info">
        {labels.emailPrefix} <strong>{email}</strong>
      </Alert>

      {error && (
        <div style={{ marginTop: 'var(--space-4, 16px)' }}>
          <Alert variant="error" data-testid="first-access-error">
            {error}
          </Alert>
        </div>
      )}

      {qrSvg ? (
        <div
          data-testid="first-access-qr"
          style={{
            background: 'white',
            padding: 16,
            borderRadius: 8,
            maxWidth: 240,
            margin: 'var(--space-4, 16px) auto',
          }}
          dangerouslySetInnerHTML={{ __html: qrSvg }}
        />
      ) : otpAuthUri ? (
        <p style={{ fontSize: '0.875rem', marginTop: 'var(--space-4, 16px)' }}>
          {labels.qrUnavailable}
        </p>
      ) : (
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--text-tertiary)',
            marginTop: 'var(--space-4, 16px)',
          }}
        >
          {labels.qrNotProvided}
        </p>
      )}

      {otpAuthUri && (
        <details style={{ margin: '8px 0 16px' }}>
          <summary style={{ cursor: 'pointer', fontSize: '0.8125rem' }}>
            {labels.otpCopyLabel}
          </summary>
          <code
            style={{
              display: 'block',
              background: 'var(--surface-inset)',
              padding: 8,
              borderRadius: 4,
              wordBreak: 'break-all',
              fontSize: '0.75rem',
              marginTop: 4,
              fontFamily: 'var(--font-mono, monospace)',
            }}
          >
            {otpAuthUri}
          </code>
        </details>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} aria-label={labels.title}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4, 16px)' }}>
          <Field label={labels.passwordLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={minLength}
              />
            )}
          </Field>
          <Field label={labels.confirmLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={minLength}
              />
            )}
          </Field>
          <Field label={labels.totpLabel}>
            {({ id }) => (
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                mono
                data-testid="fa-totp"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
              />
            )}
          </Field>
        </div>

        <div style={{ marginTop: 'var(--space-4, 16px)' }}>
          <Button type="submit" block loading={submitting} data-testid="fa-submit">
            {submitting ? labels.submitting : labels.submit}
          </Button>
        </div>
      </form>
    </Card>
  );
}
